'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import LockIcon from '@/assets/icons/account/lock.svg';
import Subtract from '@/assets/icons/subtract.svg';
import { CountdownTimer } from '@/components/shared/Countdown';
import { HoverPopover } from '@/components/shared/HoverPopover';
import { P } from '@/components/shared/Typography';
import { useCountdownRenderer } from '@/lib/utils/getCountdownRenderer';
import { getPresaleEndMs } from '@/lib/utils/getPresaleEndMs';

interface I_PresaleCountdownProps {
  presaleEndDate?: string;
}

export function PresaleCountdown({ presaleEndDate }: I_PresaleCountdownProps) {
  const tPools = useTranslations('pools');

  const presaleEndMs = useMemo(
    () => getPresaleEndMs(presaleEndDate),
    [presaleEndDate]
  );

  const countdownRenderer = useCountdownRenderer(tPools);

  // Check if presale has ended: if presaleEndMs is null or if the end date is in the past
  const isPresaleEnded = useMemo(() => {
    if (presaleEndMs === null) return true;
    return new Date(presaleEndMs) < new Date();
  }, [presaleEndMs]);

  // Don't show countdown if presale has ended or if presaleEndMs is invalid
  if (presaleEndMs === null || isPresaleEnded) {
    return null;
  }

  return (
    <div className="mb-8 p-3 bg-countdown-bg rounded-2xl flex items-center justify-center gap-3">
      <LockIcon className="w-5 h-6" />
      <CountdownTimer
        date={presaleEndMs}
        renderer={countdownRenderer}
        className="flex justify-center text-center"
      />
      <HoverPopover
        trigger={
          <Subtract className="cursor-pointer text-secondary hover:text-primary-hover active:text-primary-hover focus:text-primary-hover touch-manipulation transition-colors" />
        }
        side="bottom"
        align="end"
        sideOffset={10}
        alignOffset={-10}
      >
        <P className="text-xs font-semibold leading-normal text-popover-foreground">
          {tPools('unlockDescription')}
        </P>
      </HoverPopover>
    </div>
  );
}
